/**
 * Swipe Navigation Hook
 * 
 * Handles touch gestures for page navigation on touch devices (iPad, mobile).
 */

import { useState, useCallback } from 'react';

// Swipe detection thresholds
const MIN_SWIPE_DISTANCE = 40;    // Minimum distance in pixels
const MAX_SWIPE_DURATION = 350;   // Maximum duration in milliseconds

/**
 * Custom hook for swipe-based navigation
 * 
 * @param {function} onSwipeLeft - Callback for left swipe (next page)
 * @param {function} onSwipeRight - Callback for right swipe (previous page)
 * @returns {object} Touch event handlers
 */
export function useSwipeNavigation(onSwipeLeft, onSwipeRight) {
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [startTime, setStartTime] = useState(null);
  
  /**
   * Handle touch start event
   */
  const handleTouchStart = useCallback((event) => {
    setTouchEnd(null);
    setTouchStart(event.targetTouches[0].clientX);
    setStartTime(Date.now());
  }, []);
  
  /**
   * Handle touch move event
   */
  const handleTouchMove = useCallback((event) => {
    setTouchEnd(event.targetTouches[0].clientX);
  }, []);
  
  /**
   * Handle touch end event and determine if swipe occurred
   */
  const handleTouchEnd = useCallback(() => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const duration = Date.now() - startTime;
    
    // Check if gesture qualifies as a swipe
    if (duration > MAX_SWIPE_DURATION) {
      return;
    }
    
    const isLeftSwipe = distance > MIN_SWIPE_DISTANCE;
    const isRightSwipe = distance < -MIN_SWIPE_DISTANCE;
    
    if (isLeftSwipe) {
      onSwipeLeft?.();
    } else if (isRightSwipe) {
      onSwipeRight?.();
    }
  }, [touchStart, touchEnd, startTime, onSwipeLeft, onSwipeRight]);
  
  return {
    onTouchStart: handleTouchStart,
    onTouchMove: handleTouchMove,
    onTouchEnd: handleTouchEnd,
  };
}


